import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { contractsAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency, getDaysUntil } from '@/lib/utils';
import { FileText, DollarSign, AlertTriangle, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function Dashboard() {
  const user = JSON.parse(localStorage.getItem('ccip_user') || '{}');

  const { data: contracts30, isLoading: loading30 } = useQuery({
    queryKey: ['expiring-30'],
    queryFn: () => contractsAPI.getExpiringSummary(30).then((res) => res.data.data),
  });

  const { data: contracts60, isLoading: loading60 } = useQuery({
    queryKey: ['expiring-60'],
    queryFn: () => contractsAPI.getExpiringSummary(60).then((res) => res.data.data),
  });

  const { data: contracts90, isLoading: loading90 } = useQuery({
    queryKey: ['expiring-90'],
    queryFn: () => contractsAPI.getExpiringSummary(90).then((res) => res.data.data),
  });

  const { data: allContracts, isLoading: loadingAll } = useQuery({
    queryKey: ['all-contracts'],
    queryFn: () => contractsAPI.getAll({ status: 'ACTIVE', limit: 100 }).then((res) => res.data),
  });

  const totalActiveValue = allContracts?.data?.reduce((sum, c) => sum + c.annual_value, 0) || 0;
  const atRiskContracts =
    allContracts?.data?.filter((c) => c.competitiveness_score && c.competitiveness_score < 50) || [];
  const atRiskValue = atRiskContracts.reduce((sum, c) => sum + c.annual_value, 0);

  const stats = [
    {
      title: 'Active Contracts',
      value: allContracts?.meta?.total || 0,
      icon: FileText,
      color: 'from-[#09409C] to-[#146CBB]',
      loading: loadingAll,
    },
    {
      title: 'Total Portfolio Value',
      value: formatCurrency(totalActiveValue),
      icon: DollarSign,
      color: 'from-[#229644] to-[#1a7533]',
      loading: loadingAll,
    },
    {
      title: 'At-Risk Contracts',
      value: atRiskContracts.length,
      subtitle: formatCurrency(atRiskValue),
      icon: AlertTriangle,
      color: 'from-[#FF521C] to-[#cc4116]',
      loading: loadingAll,
    },
    {
      title: 'Expiring in 90 Days',
      value: contracts90?.count || 0,
      subtitle: formatCurrency(contracts90?.total_value || 0),
      icon: TrendingUp,
      color: 'from-[#146CBB] to-[#09409C]',
      loading: loading90,
    },
  ];

  const expiringData = [
    { label: '30 Days', days: 30, count: contracts30?.count || 0, value: contracts30?.total_value || 0, loading: loading30 },
    { label: '60 Days', days: 60, count: contracts60?.count || 0, value: contracts60?.total_value || 0, loading: loading60 },
    { label: '90 Days', days: 90, count: contracts90?.count || 0, value: contracts90?.total_value || 0, loading: loading90 },
  ];

  return (
    <div className="p-8 space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Welcome back, <span className="text-gradient-primary">{user.first_name}</span>
        </h1>
        <p className="text-muted-foreground text-lg">
          Here's an overview of your contract portfolio
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="overflow-hidden relative group hover:shadow-lg transition-shadow" data-testid={`stat-card-${index}`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription className="text-sm font-medium">{stat.title}</CardDescription>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color}`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {stat.loading ? (
                  <div className="h-8 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <>
                    <div className="text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {stat.value}
                    </div>
                    {stat.subtitle && <p className="text-sm text-muted-foreground">{stat.subtitle}</p>}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Expiring Contracts */}
      <Card data-testid="expiring-contracts-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Expiring Contracts Timeline
              </CardTitle>
              <CardDescription className="mt-2">
                Track contracts nearing their renewal dates
              </CardDescription>
            </div>
            <Link to="/contracts?status=ACTIVE">
              <Button variant="outline" className="group">
                View All
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {expiringData.map((item, index) => (
              <Link
                key={index}
                to={`/contracts?expiring_days=${item.days}`}
                className="p-6 rounded-xl border-2 border-gray-200 hover:border-[#09409C] transition-all hover:shadow-md cursor-pointer"
                data-testid={`expiring-timeline-${index}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    {item.label}
                  </span>
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF521C] to-[#cc4116]" />
                </div>
                {item.loading ? (
                  <div className="space-y-2">
                    <div className="h-8 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </div>
                ) : (
                  <>
                    <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {item.count}
                    </div>
                    <p className="text-sm text-muted-foreground">{formatCurrency(item.value)}</p>
                  </>
                )}
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent At-Risk Contracts */}
      {atRiskContracts.length > 0 && (
        <Card data-testid="at-risk-contracts-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <AlertTriangle className="w-6 h-6 text-[#FF521C] mr-2" />
                  At-Risk Contracts
                </CardTitle>
                <CardDescription className="mt-2">
                  Contracts with competitiveness scores below 50
                </CardDescription>
              </div>
              <Link to="/contracts">
                <Button variant="outline">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {atRiskContracts.slice(0, 5).map((contract) => (
                <Link
                  key={contract.id}
                  to={`/contracts/${contract.id}`}
                  className="flex items-center justify-between p-4 rounded-lg border hover:border-[#09409C] hover:shadow-md transition-all group"
                  data-testid={`at-risk-contract-${contract.id}`}
                >
                  <div className="flex-1">
                    <p className="font-semibold group-hover:text-[#09409C] transition-colors">
                      {contract.account_name}
                    </p>
                    <p className="text-sm text-muted-foreground">Contract #{contract.contract_number}</p>
                  </div>
                  <div className="text-right mr-4">
                    <p className="font-semibold">{formatCurrency(contract.annual_value)}</p>
                    <p className="text-sm text-muted-foreground">
                      {getDaysUntil(contract.end_date)} days remaining
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      Score: {contract.competitiveness_score || 'N/A'}
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#09409C] group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
